const requireEnv = (key: string): string => {
  const value = process.env[key];
  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
};

export const AUTH0_DOMAIN = requireEnv("REACT_APP_AUTH0_DOMAIN");
export const AUTH0_CLIENT_ID = requireEnv("REACT_APP_AUTH0_CLIENT_ID");
export const AUTH0_CALLBACK_URL = requireEnv("REACT_APP_AUTH0_CALLBACK_URL");
export const AUTH0_AUDIENCE = requireEnv("REACT_APP_AUTH0_AUDIENCE");

export const API_URL = requireEnv("REACT_APP_API_URL");

/**
 * Where assets are served from when `REACT_APP_CDN_BASE` says nothing.
 *
 * A hardcoded hostname is the right default here specifically because there is **one** bucket and
 * **one** distribution for both stages — `S3Bucket` is not overridden per stage in
 * `lambda/samconfig.toml`, so there is no dev/prod value to get wrong. Contrast Auth0 and
 * `API_URL`, which genuinely differ per environment and so use `requireEnv`.
 */
const DEFAULT_CDN_BASE = "https://cdn.entornobiai.com";

/**
 * Base URL for product images and other bucket assets.
 *
 * Still **optional**, but the unset default is now the CDN rather than the S3 origin (EB-517).
 * The old default — "carry on exactly as before", falling back to the bucket — was correct while
 * public bucket reads worked. Once EB-517 removes the public policy, that fallback resolves to a
 * URL that 403s, so every `npm start` without this variable renders a page of broken images and
 * nothing says why. S3 access logs on 2026-07-27 caught exactly that: 64 anonymous reads of
 * `landing-page/*` and `banners/*` carrying `Referer: http://localhost:3000/`.
 *
 * Overriding still works in both directions, so the escape hatch survives — it just inverts. To
 * point a local build back at the bucket, set `REACT_APP_CDN_BASE` to the S3 origin explicitly
 * (`LEGACY_S3_BASE` in `src/utils/assets.ts` is that value) instead of relying on it being unset.
 *
 * In prod and preview this changes nothing: both Vercel projects already set the variable, so the
 * default is only ever reached by local builds and tests.
 */
export const CDN_BASE = (process.env.REACT_APP_CDN_BASE || DEFAULT_CDN_BASE).replace(/\/+$/, "");

/**
 * CloudFront in front of `GET /items` — e.g. `https://cdn.entornobiai.com` (EB-507).
 *
 * Optional for the same reason `CDN_BASE` is: unset means the read goes straight to API Gateway,
 * exactly as before. Set, the storefront's catalog fetch is answered from the edge, compressed —
 * API Gateway HTTP APIs send no `Content-Encoding` at all, so that compression is the entire point
 * (507 KB → ~50-60 KB at 2,129 rows).
 *
 * **Reads only.** Writes stay on `API_URL` and never pass through the edge, which is why the
 * distribution allows nothing but GET and HEAD.
 */
export const CATALOG_CDN_BASE = (process.env.REACT_APP_CATALOG_CDN_BASE || "").replace(/\/+$/, "");

/**
 * Where a catalog read should go. Returns a path when no CDN is configured, so `fetchBaseQuery`
 * resolves it against `API_URL` as it always has, and an absolute URL when one is — which
 * `fetchBaseQuery` uses verbatim.
 */
export const catalogUrl = (path: string): string =>
  CATALOG_CDN_BASE ? `${CATALOG_CDN_BASE}/${path.replace(/^\/+/, "")}` : path;

/**
 * The static catalog written by the sync and by every admin write (EB-508), or `null` when no CDN
 * is configured and the API is the only source.
 *
 * It rides the same switch as `catalogUrl` on purpose: the file and the API behaviour live on one
 * hostname, and a second variable would only create a state where one is on and the other is not.
 * The storefront falls back to the API whenever this cannot be fetched, so "configured but not yet
 * published" is a supported state rather than an outage.
 */
export const CATALOG_FILE_URL = CATALOG_CDN_BASE ? `${CATALOG_CDN_BASE}/catalog.json` : null;

/**
 * The published delivery zones, rebuilt by every admin write to the zone table, or `null` when no
 * CDN is configured.
 *
 * Same switch and same fallback as `CATALOG_FILE_URL`. The storefront needs this on the *product*
 * page, not only in checkout — a shipping cost discovered on the last screen is the largest single
 * cause of checkout abandonment (docs/ordering-flow/README.md §4.1) — so it is fetched on
 * essentially every visit, for data that changes a few times a year.
 */
export const DELIVERY_ZONES_FILE_URL = CATALOG_CDN_BASE
  ? `${CATALOG_CDN_BASE}/delivery-zones.json`
  : null;

export const EDUCATION_WHATSAPP = process.env.REACT_APP_EDUCATION_WHATSAPP || "";

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// Tiendas Biai (TBS) — the POS and management system.
//
// It shares this repository and this build with the storefront and nothing else: a different API, a
// different Auth0 tenant, a different lifecycle. Everything below is **optional**, and unset means
// TBS is not part of this build at all — which is the state every existing deployment is in today.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Whether the TBS routes exist in this build.
 *
 * A feature flag rather than a separate bundle, because decision #15 chose one build with lazy
 * routes for the MVP. Unset is the important case: `/gestion/*` must not exist on the storefront's
 * deployments, and "the route 404s" is a much better failure than "the route loads and every call
 * fails" — a half-configured build that renders a POS pointing at nothing is worse than no POS.
 */
export const TBS_ENABLED = process.env.REACT_APP_TBS_ENABLED === "true";

/**
 * The TBS API. Empty unless configured, and nothing reads it unless `TBS_ENABLED`.
 *
 * Deliberately **not** `requireEnv`: the storefront builds without it, and making it required would
 * break every existing deployment the moment this file merged.
 */
export const TBS_API_URL = process.env.REACT_APP_TBS_API_URL || "";

/**
 * The company to act for, sent as `X-Tenant-Id`.
 *
 * Almost always unset, and that is correct: with one membership the authorizer picks the company
 * itself (§6.2), so a single-company user never has to think about this. It exists for the
 * multi-company case, where the authorizer refuses to guess and answers 400 — and for a build
 * pinned to one company on purpose.
 *
 * Sending an empty or wrong value would be worse than sending none: naming a company the caller does
 * not belong to is a refusal, not a fallback.
 */
export const TBS_TENANT_ID = process.env.REACT_APP_TBS_TENANT_ID || "";
